import { Box } from '@mui/material'
import CompactDaysInput from 'components/cattles/treatmentPage/components/fields/CompactDaysInput'
import { TreatmentFormFields } from 'components/cattles/treatmentPage/types/types'
import {
	TREATMENT_SCHEMA_DESCRIPTION_MAX_LENGTH,
	TREATMENT_SCHEMA_TITLE_MAX_LENGTH,
} from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import AutocompleteInput from 'components/shared/textFields/AutocompleteInput'
import Input from 'components/shared/textFields/Input'
import SwitchiOS from 'ui/SwitchiOS'
import { TypographyHeader14 } from 'ui/typography/TypographyHeader14'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'

interface Props {
	index: number
	field: TreatmentFormFields
	diagnosesList: { id: number; title: string }[]
	loading: boolean
	shouldShowError: (field: keyof TreatmentFormFields, index: number) => boolean
	onChange: (
		fieldName: keyof TreatmentFormFields,
		value: string | number[] | boolean,
	) => void
}

const toggleDiagnose = (selected: number[], id: number) =>
	selected.includes(id)
		? selected.filter(item => item !== id)
		: [...selected, id]

const TreatmentFieldsBlock = ({
	index,
	field,
	diagnosesList,
	loading,
	shouldShowError,
	onChange,
}: Props) => {
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
				<TypographyHeader14>Диагноз</TypographyHeader14>
				<AutocompleteInput
					label='Выберите диагноз'
					widthForInput='100%'
					isMultiple
					isCheckbox
					needChip
					isShowTags={false}
					variantSize='medium'
					availableList={diagnosesList}
					selectedValues={field.selectedDiagnose}
					loading={loading}
					onClick={(id: number) =>
						onChange(
							'selectedDiagnose',
							toggleDiagnose(field.selectedDiagnose || [], id),
						)
					}
					isError={shouldShowError('selectedDiagnose', index)}
				/>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
				<TypographyHeader14>Схема лечения</TypographyHeader14>
				<Input
					isError={shouldShowError('title', index)}
					value={field.title}
					onChange={e => onChange('title', e.target.value)}
					label='Наименование схемы лечения'
					width='100%'
					isDisable={false}
					isMultiline={false}
					maxLength={TREATMENT_SCHEMA_TITLE_MAX_LENGTH}
				/>
				<Input
					isError={shouldShowError('description', index)}
					value={field.description}
					onChange={e => onChange('description', e.target.value)}
					label='Описание схемы лечения'
					width='100%'
					height='auto'
					isDisable={false}
					isMultiline
					minRows={3}
					maxRows={16}
					maxLength={TREATMENT_SCHEMA_DESCRIPTION_MAX_LENGTH}
				/>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
				<TypographyHeader14>Продолжительность лечения</TypographyHeader14>
				<CompactDaysInput
					value={field.duration}
					onChange={value => onChange('duration', value)}
					isError={shouldShowError('duration', index)}
				/>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
				<TypographyHeader14>Периодичность осмотров</TypographyHeader14>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						flexWrap: 'wrap',
					}}
				>
					<TypographyPrimary>Проводить осмотр каждый</TypographyPrimary>
					<CompactDaysInput
						value={field.examinationInterval}
						onChange={value => onChange('examinationInterval', value)}
						isError={shouldShowError('examinationInterval', index)}
						placeholder='0 день'
						unitLabel='день'
					/>
					<TypographyPrimary>в период лечения.</TypographyPrimary>
				</Box>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
					}}
				>
					<TypographyHeader14>Забраковать молоко на период</TypographyHeader14>
					<SwitchiOS
						checked={Boolean(field.rejectMilk)}
						background='#65C466'
						onClick={() => {
							const nextValue = !field.rejectMilk
							onChange('rejectMilk', nextValue)
							if (!nextValue) onChange('milkRejectDays', '')
						}}
					/>
				</Box>
				{field.rejectMilk && (
					<>
						<TypographySecondary>
							Укажите период выбраковки.
						</TypographySecondary>
						<CompactDaysInput
							value={field.milkRejectDays}
							onChange={value => onChange('milkRejectDays', value)}
							isError={shouldShowError('milkRejectDays', index)}
						/>
					</>
				)}
			</Box>
		</Box>
	)
}

export default TreatmentFieldsBlock
